<script setup>
/* global axios */
// [AstraChat] Central de API: cofre (chaves cadastradas aqui — CRUD) + agregador
// (chaves que já existem noutros módulos: IA, Meta CAPI — só listadas, mascaradas).
// Self-contained (axios direto), visual nativo de Settings.
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const route = useRoute();
const base = `/api/v1/accounts/${route.params.accountId}/kanban/api_keys`;

const vault = ref([]);
const aggregated = ref([]);
const loading = ref(true);

const showForm = ref(false);
const saving = ref(false);
const editing = ref(null);
const form = ref({ name: '', service: '', token: '', notes: '' });

const ORIGIN_LABEL = {
  ia: 'Inteligência Artificial',
  oauth: 'IA (OAuth)',
  meta_capi: 'Meta CAPI',
};

const load = async () => {
  loading.value = true;
  try {
    const { data } = await axios.get(base);
    vault.value = data?.data?.vault || [];
    aggregated.value = data?.data?.aggregated || [];
  } catch (e) {
    useAlert('Não consegui carregar as chaves.');
  } finally {
    loading.value = false;
  }
};

const openNew = () => {
  editing.value = null;
  form.value = { name: '', service: '', token: '', notes: '' };
  showForm.value = true;
};
const openEdit = k => {
  editing.value = k;
  // token não vem inteiro do backend — em branco = mantém o atual.
  form.value = { name: k.name, service: k.service || '', token: '', notes: k.notes || '' };
  showForm.value = true;
};
const closeForm = () => {
  showForm.value = false;
};

const save = async () => {
  if (!form.value.name.trim()) return;
  saving.value = true;
  try {
    if (editing.value) {
      await axios.patch(`${base}/${editing.value.id}`, form.value);
    } else {
      await axios.post(base, form.value);
    }
    showForm.value = false;
    await load();
    useAlert('Chave salva.');
  } catch (e) {
    useAlert(e?.response?.data?.errors?.[0] || 'Não consegui salvar.');
  } finally {
    saving.value = false;
  }
};

const remove = async k => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(`Remover a chave "${k.name}"?`)) return;
  try {
    await axios.delete(`${base}/${k.id}`);
    await load();
    useAlert('Chave removida.');
  } catch (e) {
    useAlert('Não consegui remover.');
  }
};

const hasAny = computed(() => vault.value.length || aggregated.value.length);

onMounted(load);
</script>

<template>
  <SettingsLayout :is-loading="loading" :no-records-found="false">
    <template #header>
      <BaseSettingsHeader
        title="Central de API"
        description="Reúna num só lugar as chaves de API que você usa. Cadastre novas no cofre e veja as que já estão conectadas em outros módulos."
      >
        <template #actions>
          <Button label="Nova chave" icon="i-lucide-plus" size="sm" @click="openNew" />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="flex flex-col gap-8">
        <!-- COFRE -->
        <section class="flex flex-col gap-3">
          <div class="flex items-center gap-2">
            <span class="i-lucide-key-round size-4 text-n-slate-11" />
            <h3 class="text-heading-3 text-n-slate-12">Meu cofre</h3>
            <span class="text-xs text-n-slate-10">{{ vault.length }}</span>
          </div>

          <p v-if="!vault.length" class="text-sm text-n-slate-11 py-2">
            Nenhuma chave cadastrada ainda. Use “Nova chave” para guardar uma.
          </p>

          <div v-else class="border rounded-xl border-n-weak divide-y divide-n-weak overflow-hidden">
            <div
              v-for="k in vault"
              :key="k.id"
              class="flex items-center gap-4 px-4 py-3 bg-n-solid-1"
            >
              <div class="flex flex-col flex-1 min-w-0">
                <span class="text-sm font-medium truncate text-n-slate-12">{{ k.name }}</span>
                <span class="text-xs text-n-slate-11 truncate">
                  {{ k.service || 'Sem serviço' }}<template v-if="k.notes"> · {{ k.notes }}</template>
                </span>
              </div>
              <span class="font-mono text-xs text-n-slate-11">{{ k.masked || '—' }}</span>
              <div class="flex gap-1 shrink-0">
                <Button icon="i-lucide-pencil" slate xs faded @click="openEdit(k)" />
                <Button
                  icon="i-lucide-trash-2"
                  slate
                  xs
                  faded
                  class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                  @click="remove(k)"
                />
              </div>
            </div>
          </div>
        </section>

        <!-- AGREGADOR -->
        <section v-if="aggregated.length" class="flex flex-col gap-3">
          <div class="flex items-center gap-2">
            <span class="i-lucide-link size-4 text-n-slate-11" />
            <h3 class="text-heading-3 text-n-slate-12">Chaves conectadas</h3>
            <span class="text-xs text-n-slate-10">{{ aggregated.length }}</span>
          </div>
          <p class="-mt-1 text-xs text-n-slate-10">
            Já configuradas em outros módulos. Mostradas aqui para você ter a visão completa
            (edite-as no módulo de origem).
          </p>

          <div class="border rounded-xl border-n-weak divide-y divide-n-weak overflow-hidden">
            <div
              v-for="(a, i) in aggregated"
              :key="`${a.source}-${a.ref}-${i}`"
              class="flex items-center gap-4 px-4 py-3 bg-n-alpha-1"
            >
              <div class="flex flex-col flex-1 min-w-0">
                <span class="text-sm font-medium truncate text-n-slate-12">{{ a.name }}</span>
                <span class="text-xs text-n-slate-11 truncate">{{ a.service }}</span>
              </div>
              <span
                class="text-[0.625rem] uppercase tracking-wide px-2 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-11"
              >
                {{ ORIGIN_LABEL[a.source] || a.source }}
              </span>
              <span class="font-mono text-xs text-n-slate-11">{{ a.masked || '—' }}</span>
            </div>
          </div>
        </section>

        <p v-if="!hasAny && !loading" class="text-sm text-center text-n-slate-11 py-10">
          Nenhuma chave por aqui ainda.
        </p>
      </div>

      <!-- FORM (add/edit) -->
      <teleport to="body">
        <div
          v-if="showForm"
          class="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
          @click.self="closeForm"
        >
          <div class="w-full max-w-md p-6 border shadow-xl rounded-xl bg-n-solid-1 border-n-weak flex flex-col gap-4">
            <div class="flex items-center justify-between">
              <h3 class="text-heading-3 text-n-slate-12">
                {{ editing ? 'Editar chave' : 'Nova chave' }}
              </h3>
              <button
                type="button"
                class="grid size-7 place-items-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
                @click="closeForm"
              >
                <span class="i-lucide-x size-4" />
              </button>
            </div>

            <label class="flex flex-col gap-1 text-sm text-n-slate-11">
              Nome
              <input
                v-model="form.name"
                type="text"
                placeholder="Ex.: OpenAI do n8n"
                class="px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </label>
            <label class="flex flex-col gap-1 text-sm text-n-slate-11">
              Serviço
              <input
                v-model="form.service"
                type="text"
                placeholder="Ex.: OpenAI, Stripe, Google…"
                class="px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </label>
            <label class="flex flex-col gap-1 text-sm text-n-slate-11">
              Chave
              <input
                v-model="form.token"
                type="password"
                autocomplete="new-password"
                :placeholder="editing ? 'Deixe em branco para manter a atual' : 'Cole a chave aqui'"
                class="px-3 py-2 font-mono border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </label>
            <label class="flex flex-col gap-1 text-sm text-n-slate-11">
              Notas (opcional)
              <input
                v-model="form.notes"
                type="text"
                placeholder="Onde é usada, validade…"
                class="px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </label>

            <div class="flex justify-end gap-2 pt-1">
              <Button label="Cancelar" slate faded sm @click="closeForm" />
              <Button
                :label="saving ? 'Salvando…' : 'Salvar'"
                sm
                :disabled="saving || !form.name.trim()"
                @click="save"
              />
            </div>
          </div>
        </div>
      </teleport>
    </template>
  </SettingsLayout>
</template>
